import React, { createContext, useContext, useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { visit } from "unist-util-visit";
import { NavLink, useParams } from "react-router-dom";
import { FileIcon } from "../FileIcon";
import { links } from "@/src/utils/links";
import {
  FileType,
  PermissionsResourceType,
  useDataRoomFileQuery,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { PermissionCheck } from "../permissions/PermissionCheck";
import {
  rectToExcelRange,
  rectToExcelCell,
} from "@/src/utils/rectToExcelRange";
import { FolderIcon, PlusIcon, CheckIcon } from "lucide-react";

export interface InlineFileClickData {
  fileId: string;
  fileName: string;
  fileType: FileType;
  folderId: string;
  pageIndex?: number;
  rectsOnPage?: number[][];
}

export function fileRefKey(data: {
  fileId: string;
  pageIndex?: number;
  rectsOnPage?: number[][];
}): string {
  const parts = [data.fileId];
  if (data.pageIndex !== undefined) parts.push(`p${data.pageIndex}`);
  if (data.rectsOnPage && data.rectsOnPage.length > 0) {
    parts.push(`r${JSON.stringify(data.rectsOnPage)}`);
  }
  return parts.join(":");
}

export interface InlineFileActions {
  onFileClick: (data: InlineFileClickData) => void;
  onAddFile: (data: InlineFileClickData) => void;
  addedFileKeys: Set<string>;
}

const InlineFileActionsContext = createContext<InlineFileActions | null>(null);

export const InlineFileActionsProvider = InlineFileActionsContext.Provider;

const INLINE_REF_RE =
  /\{\{dtrmfile:(dtrmfile_[A-Za-z0-9]+)([^}]*)\}\}|\{\{dtrmfolder:(dtrmfolder_[A-Za-z0-9]+)\}\}/g;

function parseRefParams(raw: string): Record<string, string> {
  const params: Record<string, string> = {};
  if (!raw) return params;
  const segments = raw.split("|").filter(Boolean);
  for (const seg of segments) {
    const colonIdx = seg.indexOf(":");
    if (colonIdx > 0) {
      params[seg.slice(0, colonIdx)] = seg.slice(colonIdx + 1);
    }
  }
  return params;
}

export function remarkInlineRefs() {
  return (tree: any) => {
    visit(tree, "text", (node: any, index: number | undefined, parent: any) => {
      if (index === undefined || !parent) return;

      const value: string = node.value;
      INLINE_REF_RE.lastIndex = 0;
      if (!INLINE_REF_RE.test(value)) return;
      INLINE_REF_RE.lastIndex = 0;

      const newNodes: any[] = [];
      let lastIdx = 0;

      for (const match of value.matchAll(INLINE_REF_RE)) {
        const start = match.index!;
        if (start > lastIdx) {
          newNodes.push({ type: "text", value: value.slice(lastIdx, start) });
        }

        if (match[1]) {
          const params = parseRefParams(match[2]);
          newNodes.push({
            type: "fileRef",
            data: {
              hName: "fileref",
              hProperties: {
                fileId: match[1],
                pageIndex: params.pageIndex ?? "",
                rectsOnPage: params.rectsOnPage ?? "",
                sheetIndex: params.sheetIndex ?? "",
                row0: params.row0 ?? "",
                col0: params.col0 ?? "",
                row1: params.row1 ?? "",
                col1: params.col1 ?? "",
              },
            },
          });
        } else if (match[3]) {
          newNodes.push({
            type: "folderRef",
            data: {
              hName: "folderref",
              hProperties: {
                folderId: match[3],
              },
            },
          });
        }

        lastIdx = start + match[0].length;
      }

      if (lastIdx < value.length) {
        newNodes.push({ type: "text", value: value.slice(lastIdx) });
      }

      parent.children.splice(index, 1, ...newNodes);
    });
  };
}

function AddFileButton(props: {
  isAdded: boolean;
  onClick: (e: React.MouseEvent) => void;
}) {
  const [justAdded, setJustAdded] = useState(false);
  const showCheck = props.isAdded || justAdded;

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (props.isAdded) return;
      setJustAdded(true);
      props.onClick(e);
      setTimeout(() => setJustAdded(false), 1500);
    },
    [props.isAdded, props.onClick],
  );

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`inline-flex items-center justify-center w-4 h-4 rounded-full flex-shrink-0 transition-all duration-150 ${
        showCheck
          ? "bg-green-100 text-green-600"
          : "bg-persian-100 text-persian-600 opacity-0 group-hover/pill:opacity-100 hover:bg-persian-200"
      }`}
      title={showCheck ? "Added to answer" : "Add to answer"}
    >
      {showCheck ? (
        <CheckIcon className="w-2.5 h-2.5" />
      ) : (
        <PlusIcon className="w-2.5 h-2.5" />
      )}
    </button>
  );
}

export function InlineFilePill(props: {
  fileId: string;
  pageIndex?: number;
  rectsOnPage?: number[][];
  sheetIndex?: number;
  cell?: string;
}) {
  const client = useGqlClient();
  const { dealId } = useParams<{ dealId: string }>();
  const actions = useContext(InlineFileActionsContext);
  const fileQuery = useDataRoomFileQuery(
    client,
    { id: props.fileId },
    {
      enabled: props.fileId !== "",
      queryKey: ["InlineFilePill", { id: props.fileId }],
    },
  );

  const name = fileQuery.data?.dataRoomFile.name ?? props.fileId;
  const fileType = fileQuery.data?.dataRoomFile.fileType ?? FileType.Other;
  const folderId = fileQuery.data?.dataRoomFile.folderId ?? "";

  let locationLabel: string | null = null;
  if (props.pageIndex !== undefined) {
    locationLabel = `p.${props.pageIndex + 1}`;
  } else if (props.cell) {
    locationLabel = props.cell;
  }

  const fileClickData: InlineFileClickData = {
    fileId: props.fileId,
    fileName: name,
    fileType,
    folderId,
    pageIndex: props.pageIndex,
    rectsOnPage: props.rectsOnPage,
  };

  const content = (
    <span className="inline-flex items-center align-middle gap-x-0.5 max-w-[14rem] text-persian-700 hover:text-persian-900 transition-colors">
      <FileIcon fileType={fileType} size="s" />
      <span
        className="text-sm font-medium underline decoration-persian-300 underline-offset-2 hover:decoration-persian-600 truncate"
        title={name}
      >
        {name}
      </span>
      {locationLabel ? (
        <span className="text-xs text-gray-400 no-underline flex-shrink-0">
          {locationLabel}
        </span>
      ) : null}
    </span>
  );

  if (actions) {
    const isAdded = actions.addedFileKeys.has(fileRefKey(fileClickData));
    return (
      <span className="group/pill inline-flex items-center align-middle gap-x-0.5 mx-0.5 -my-0.5">
        <button
          type="button"
          className="inline cursor-pointer"
          onClick={() => actions.onFileClick(fileClickData)}
        >
          {content}
        </button>
        <AddFileButton
          isAdded={isAdded}
          onClick={() => actions.onAddFile(fileClickData)}
        />
      </span>
    );
  }

  const hasLink = !!dealId && !!folderId;

  if (!hasLink) {
    return <span className="mx-0.5 -my-0.5">{content}</span>;
  }

  return (
    <PermissionCheck
      input={{
        permissionKey: "documents:view",
        resource: {
          resourceID: props.fileId,
          resourceType: PermissionsResourceType.Document,
        },
      }}
    >
      <NavLink
        to={links.fileLink(props.fileId, folderId, dealId, {
          pageIndex: props.pageIndex,
          rectsOnPage: props.rectsOnPage,
          sheetIndex: props.sheetIndex,
          cell: props.cell,
        })}
        className="inline"
      >
        <span className="mx-0.5 -my-0.5">{content}</span>
      </NavLink>
    </PermissionCheck>
  );
}

export function InlineFolderPill(props: { folderId: string }) {
  const { dealId } = useParams<{ dealId: string }>();
  const hasLink = !!dealId;

  const content = (
    <span className="inline-flex items-center align-middle gap-x-0.5 mx-0.5 -my-0.5 text-persian-700">
      <FolderIcon className="w-3.5 h-3.5" />
      <span className="text-sm font-medium underline decoration-persian-300 underline-offset-2">
        {props.folderId}
      </span>
    </span>
  );

  if (!hasLink) {
    return content;
  }

  return (
    <PermissionCheck
      input={{
        permissionKey: "documents:view",
        resource: {
          resourceID: props.folderId,
          resourceType: PermissionsResourceType.Folder,
        },
      }}
    >
      <NavLink
        to={links.folderLink(props.folderId, dealId)}
        className="inline"
      >
        {content}
      </NavLink>
    </PermissionCheck>
  );
}

export const markdownRefComponents: Record<string, React.ComponentType<any>> = {
  fileref: (props: any) => {
    const fileId: string = props.fileId || "";
    const rawPage: string = props.pageIndex || "";
    const pageIndex = rawPage !== "" ? parseInt(rawPage, 10) : undefined;
    const rawRects: string = props.rectsOnPage || "";
    let rectsOnPage: number[][] | undefined;
    if (rawRects) {
      try {
        const parsed = JSON.parse(rawRects);
        if (Array.isArray(parsed)) {
          rectsOnPage = parsed;
        }
      } catch { /* ignore malformed */ }
    }

    const rawSheet: string = props.sheetIndex || "";
    const sheetIndex = rawSheet !== "" ? parseInt(rawSheet, 10) : undefined;

    let cell: string | undefined;
    const rawRow0: string = props.row0 || "";
    const rawCol0: string = props.col0 || "";
    if (rawRow0 !== "" && rawCol0 !== "") {
      const row0 = parseInt(rawRow0, 10);
      const col0 = parseInt(rawCol0, 10);
      const rawRow1: string = props.row1 || "";
      const rawCol1: string = props.col1 || "";
      if (rawRow1 !== "" && rawCol1 !== "") {
        cell = rectToExcelRange({ row0, col0, row1: parseInt(rawRow1, 10), col1: parseInt(rawCol1, 10) });
      } else {
        cell = rectToExcelCell({ row0, col0 });
      }
    }

    return (
      <InlineFilePill
        fileId={fileId}
        pageIndex={pageIndex}
        rectsOnPage={rectsOnPage}
        sheetIndex={sheetIndex}
        cell={cell}
      />
    );
  },
  folderref: (props: any) => {
    const folderId: string = props.folderId || props["folderId"] || "";
    return <InlineFolderPill folderId={folderId} />;
  },
};

export function stripTrailingPartialRef(text: string): string {
  const lastOpen = text.lastIndexOf("{{");
  if (lastOpen === -1) return text;

  const closingAfter = text.indexOf("}}", lastOpen);
  if (closingAfter === -1) {
    return text.slice(0, lastOpen);
  }
  return text;
}

const STRIP_REFS_RE =
  /\{\{dtrmfile:dtrmfile_[A-Za-z0-9]+[^}]*\}\}|\{\{dtrmfolder:dtrmfolder_[A-Za-z0-9]+\}\}/g;

export function stripInlineRefs(text: string): string {
  return text.replace(STRIP_REFS_RE, "").replace(/  +/g, " ").trim();
}

export function MarkdownWithRefs(props: { text: string }) {
  const cleanText = stripTrailingPartialRef(props.text);

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkBreaks, remarkInlineRefs]}
      components={markdownRefComponents}
    >
      {cleanText}
    </ReactMarkdown>
  );
}
